import { ensureValidToken, forceRefresh } from '@/core/auth/token-manager'
import { env } from '@/core/config/env'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * The parsed error body, when there was one. Some endpoints answer a coded state rather
     * than prose — `{code, params}` — because the clients localise it and put it under the
     * field it belongs to. Without this the code is lost and only the status survives.
     */
    public body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  skipAuth?: boolean
}

/** Never throws: a failed read must not replace the status with a parse error. */
async function errorBody(response: Response): Promise<unknown> {
  try {
    return await response.clone().json()
  } catch {
    return undefined
  }
}

async function buildHeaders(
  skipAuth: boolean,
  custom?: Record<string, string>,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-app-platform': 'web',
    ...custom,
  }

  if (!skipAuth) {
    // Mirrors iOS HeaderBuilder: refreshIfNeeded() + inject token
    const token = await ensureValidToken()
    if (!token) {
      // No valid session. Do NOT fire an authed request without a token — the
      // backend records it as a tokenless "No auth token" 401 (the iOS-webview
      // membership flood). ensureValidToken already cleared the session and
      // triggered re-auth on refresh failure; surface a 401 so callers treat it
      // as unauthenticated instead of sending an Authorization-less request.
      throw new ApiError(401, 'No valid session')
    }
    headers.Authorization = `Bearer ${token}`
  }

  return headers
}

async function sendRequest(
  path: string,
  headers: Record<string, string>,
  init: Omit<RequestInit, 'headers'>,
  body?: unknown,
): Promise<Response> {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
}

/**
 * Request with 401 → refresh → retry once.
 * Mirrors iOS AuthService.sendWith401Retry().
 */
async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, skipAuth = false, headers: customHeaders, ...init } = options

  const headers = await buildHeaders(
    skipAuth,
    customHeaders as Record<string, string>,
  )

  let response = await sendRequest(path, headers, init, body)

  // 401 → refresh → retry (mirrors iOS sendWith401Retry)
  if (response.status === 401 && !skipAuth) {
    const newToken = await forceRefresh()
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`
      response = await sendRequest(path, headers, init, body)
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      response.statusText,
      await errorBody(response),
    )
  }

  if (response.status === 204) {
    return undefined as T
  }

  // void endpoints (e.g. PATCH .../change) return 200 with an empty body —
  // response.json() would throw on that, so parse only when there's content.
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const restClient = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}
